import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { resolveImageUrl } from "./ImageUploader";

const ROUTE_BY_VERTICAL = {
  real_estate: "/imoveis",
  health: "/medicos-publicacao",
  automotive: "/veiculos",
};

/**
 * Rotating rail of "boosted" (impulsionados) listings.
 * Reads from GET /api/boosted/listings and renders horizontally.
 * Used in the Feed and Home institucional to give visibility to paid boosts.
 */
export default function BoostedRail({ limit = 6, className = "" }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/boosted/listings", { params: { limit } })
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => {});
    load();
    // reshuffle rotation every 45s so different boosts get exposure per session
    const t = setInterval(load, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [limit]);

  if (!items.length) return null;

  const fmtPrice = (n) => {
    if (n == null || Number.isNaN(Number(n))) return null;
    try { return Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }
    catch { return `R$ ${n}`; }
  };

  return (
    <section data-testid="boosted-rail" className={className}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" /> Anúncios impulsionados
          </h2>
          <p className="text-sm text-muted-foreground">Destaques rotativos de corretores, médicos e lojas.</p>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        {items.map((it, i) => {
          const route = ROUTE_BY_VERTICAL[it.vertical] || "/empresas";
          const price = fmtPrice(it.price);
          const cover = it.cover || (it.images || [])[0];
          return (
            <motion.button key={it.id}
              data-testid={`boosted-item-${it.id}`}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
              onClick={() => navigate(`${route}/${it.slug}`)}
              className="w-56 shrink-0 text-left rounded-3xl overflow-hidden border-2 border-primary/40 bg-card soft-shadow hover:-translate-y-1 transition-transform">
              <div className="relative h-32">
                {cover ? (
                  <img src={resolveImageUrl(cover)} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary flex items-center justify-center">
                    <Rocket className="w-8 h-8 text-primary" />
                  </div>
                )}
                <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5">
                  <Rocket className="w-3 h-3" /> Impulsionado
                </span>
              </div>
              <div className="p-3">
                <p className="font-heading font-bold text-sm leading-tight line-clamp-1">{it.title}</p>
                {it.location && (
                  <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{it.location}</p>
                )}
                {price && <p className="text-sm font-semibold text-primary mt-1">{price}</p>}
                <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                  Ver anúncio <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
}
